/**
 * Payments configuration — method toggles, limits, fees, webhook URLs.
 *
 * Reads `GET /admin/payments/config` and writes `PATCH /admin/payments/config`.
 * The config shape is defined by `PaymentConfigService.snapshot()` on the
 * backend; this tab renders the parts an admin needs to enable methods and
 * tune limits for sandbox / production testing.
 */
import { getPaymentsConfig, updatePaymentsConfig } from '../admin-api';
import type { DepositMethodState, PaymentConfigSnapshot, PayoutMethodState } from '../admin-api.types';
import { badge, checkbox, errorText, esc, field, section } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

function methodBadge(m: { configured: boolean; enabled: boolean; active: boolean }): string {
  if (m.active) return badge('ACTIVE', 'ok');
  if (m.enabled && !m.configured) return badge('NO TERMINAL', 'bad');
  if (m.enabled) return badge('ENABLED', 'warn');
  return badge('OFF', 'mute');
}

function depositRow(m: DepositMethodState): string {
  return `
    <div class="tg-adm-card" data-method-type="deposit" data-method-id="${esc(m.id)}">
      <div class="tg-adm-card-head">
        <strong>${esc(m.label)}</strong> ${methodBadge(m)}
      </div>
      <div class="tg-adm-card-body">
        <div class="tg-adm-row">
          ${checkbox(`deposit.${m.id}.enabled`, 'Enabled', m.enabled)}
        </div>
        <div class="tg-adm-row">
          <span class="tg-adm-label">Terminal configured</span>
          <span>${m.configured ? badge('YES', 'ok') : badge('NO — set env vars in Vault', 'bad')}</span>
        </div>
        <div class="tg-adm-row">
          <span class="tg-adm-label">Currencies</span>
          <span>${m.currencies.length ? esc(m.currencies.join(', ')) : '—'}</span>
          <span class="tg-adm-hint">Options: ${esc(m.currencyOptions.join(', ') || 'none')}</span>
        </div>
        <div class="tg-adm-row">
          <span class="tg-adm-label">Billing</span>
          <span>${esc(m.billing)}</span>
        </div>
      </div>
    </div>`;
}

function payoutRow(m: PayoutMethodState): string {
  return `
    <div class="tg-adm-card" data-method-type="payout" data-method-id="${esc(m.id)}">
      <div class="tg-adm-card-head">
        <strong>${esc(m.label)}</strong> (${esc(m.receiver)}) ${methodBadge(m)}
      </div>
      <div class="tg-adm-card-body">
        <div class="tg-adm-row">
          ${checkbox(`payout.${m.id}.enabled`, 'Enabled', m.enabled)}
        </div>
        <div class="tg-adm-row">
          <span class="tg-adm-label">Terminal configured</span>
          <span>${m.configured ? badge('YES', 'ok') : badge('NO — set env vars in Vault', 'bad')}</span>
        </div>
        <div class="tg-adm-row">
          <span class="tg-adm-label">Currencies</span>
          <span>${m.currencies.length ? esc(m.currencies.join(', ')) : '—'}</span>
        </div>
        <div class="tg-adm-row">
          <span class="tg-adm-label">Status polling</span>
          <span>${m.pollable ? 'Yes' : 'Webhook-only'}</span>
        </div>
      </div>
    </div>`;
}

function limitsForm(cfg: PaymentConfigSnapshot): string {
  const l = cfg.limits;
  return `
    <form class="tg-adm-form" data-form="limits">
      <h3 class="tg-adm-label">Deposit limits</h3>
      ${field('depositMinCents', 'Min (cents)', l.depositMinCents, { type: 'number', hint: `${(l.depositMinCents / 100).toFixed(2)} ${cfg.defaultCurrency}` })}
      ${field('depositMaxCents', 'Max (cents)', l.depositMaxCents, { type: 'number', hint: `${(l.depositMaxCents / 100).toFixed(2)} ${cfg.defaultCurrency}` })}
      ${field('depositFeeBps', 'Fee (basis points)', l.depositFeeBps, { type: 'number', hint: `${l.depositFeePct}%` })}
      ${field('depositKycThresholdCents', 'KYC threshold (cents)', l.depositKycThresholdCents, { type: 'number' })}
      ${checkbox('requireVerifiedEmail', 'Require verified email for deposits', l.requireVerifiedEmail)}

      <h3 class="tg-adm-label" style="margin-top:1.5rem">Withdrawal limits</h3>
      ${field('withdrawalMinCents', 'Min (cents)', l.withdrawalMinCents, { type: 'number', hint: `${(l.withdrawalMinCents / 100).toFixed(2)} ${cfg.defaultCurrency}` })}
      ${field('withdrawalMaxCents', 'Max (cents)', l.withdrawalMaxCents, { type: 'number', hint: `${(l.withdrawalMaxCents / 100).toFixed(2)} ${cfg.defaultCurrency}` })}
      ${field('withdrawalFeeBps', 'Fee (basis points)', l.withdrawalFeeBps, { type: 'number', hint: `${l.withdrawalFeePct}%` })}
      ${checkbox('capToDeposits', 'Cap withdrawals to lifetime deposits', l.capToDeposits)}

      <div class="tg-adm-actions">
        <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save limits</button>
      </div>
    </form>`;
}

function webhookInfo(cfg: PaymentConfigSnapshot): string {
  return `
    <div class="tg-adm-form">
      <h3 class="tg-adm-label">Webhook URLs (register at Infraservedge)</h3>
      <div class="tg-adm-field">
        <span class="tg-adm-label">Deposit</span>
        <code class="tg-adm-mono">${esc(cfg.webhooks.deposit)}</code>
      </div>
      <div class="tg-adm-field">
        <span class="tg-adm-label">Payout</span>
        <code class="tg-adm-mono">${esc(cfg.webhooks.payout)}</code>
      </div>
    </div>`;
}

function invoiceInfo(cfg: PaymentConfigSnapshot): string {
  const inv = cfg.invoices;
  return `
    <form class="tg-adm-form" data-form="invoices">
      <h3 class="tg-adm-label">Invoice settings</h3>
      ${field('invoicePrefix', 'Invoice prefix', inv.prefix)}
      ${field('creditNotePrefix', 'Credit note prefix', inv.creditNotePrefix)}
      ${checkbox('invoiceEmailEnabled', 'Email invoices to players', inv.emailEnabled)}
      <div class="tg-adm-actions">
        <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save invoice settings</button>
      </div>
    </form>`;
}

export const paymentsTab: AdminTab = {
  id: 'payments',
  label: 'Payments',
  group: 'Billing',

  mount(host, ctx) {
    let live = true;
    let cfg: PaymentConfigSnapshot | null = null;
    let error = '';

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!cfg) return '<p class="tg-adm-empty">Loading…</p>';

      if (!cfg.adopted) {
        return '<p class="tg-adm-empty">Payments not adopted — useLedger() has not been called.</p>';
      }

      const globals = `
        <form class="tg-adm-form" data-form="globals">
          <h3 class="tg-adm-label">Global toggles</h3>
          ${checkbox('withdrawalsEnabled', 'Withdrawals enabled', cfg.withdrawalsEnabled)}
          ${checkbox('kycEnabled', 'KYC required for deposits above threshold', cfg.kycEnabled)}
          <div class="tg-adm-actions">
            <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save</button>
          </div>
        </form>`;

      const depositMethods = `
        <h3 class="tg-adm-label">Deposit methods</h3>
        ${cfg.deposit.map(depositRow).join('')}`;

      const payoutMethods = `
        <h3 class="tg-adm-label">Payout methods</h3>
        ${cfg.payout.map(payoutRow).join('')}`;

      return globals + depositMethods + payoutMethods + limitsForm(cfg) + webhookInfo(cfg) + invoiceInfo(cfg);
    }

    const paint = () => {
      host.innerHTML = section(
        'Payments',
        'Toggle methods, configure limits and fees. Terminal credentials are managed in Vault.',
        body(),
        '<button type="button" class="tg-adm-btn" data-act="reload">Reload</button>',
      );
    };

    async function load(): Promise<void> {
      error = '';
      try {
        cfg = await getPaymentsConfig();
        if (!live) return;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    async function saveMethodToggles(): Promise<void> {
      if (!cfg) return;
      const deposit: Record<string, { enabled: boolean }> = {};
      const payout: Record<string, { enabled: boolean }> = {};

      for (const m of cfg.deposit) {
        const cb = host.querySelector<HTMLInputElement>(`[data-method-id="${m.id}"][data-method-type="deposit"] input[type="checkbox"]`);
        if (cb) deposit[m.id] = { enabled: cb.checked };
      }
      for (const m of cfg.payout) {
        const cb = host.querySelector<HTMLInputElement>(`[data-method-id="${m.id}"][data-method-type="payout"] input[type="checkbox"]`);
        if (cb) payout[m.id] = { enabled: cb.checked };
      }

      try {
        cfg = await updatePaymentsConfig({ deposit, payout });
        if (!live) return;
        ctx.notify('Method toggles saved.');
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      paint();
    }

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-act="reload"]')) void load();

      const card = target.closest<HTMLElement>('.tg-adm-card');
      if (card && target.closest('input[type="checkbox"]')) {
        void saveMethodToggles();
      }
    };

    const onSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      e.preventDefault();

      if (form.dataset.form === 'globals') {
        const wdEnabled = form.querySelector<HTMLInputElement>('[name="withdrawalsEnabled"]')?.checked ?? false;
        const kycOn = form.querySelector<HTMLInputElement>('[name="kycEnabled"]')?.checked ?? false;
        void (async () => {
          try {
            cfg = await updatePaymentsConfig({ withdrawalsEnabled: wdEnabled, kycEnabled: kycOn });
            if (!live) return;
            ctx.notify('Global settings saved.');
          } catch (err) {
            if (!live) return;
            ctx.notify(errorText(err), 'err');
          }
          paint();
        })();
        return;
      }

      if (form.dataset.form === 'limits') {
        const int = (name: string) => {
          const v = form.querySelector<HTMLInputElement>(`[name="${name}"]`)?.value;
          return v !== undefined && v !== '' ? parseInt(v, 10) : undefined;
        };
        const cb = (name: string) =>
          form.querySelector<HTMLInputElement>(`[name="${name}"]`)?.checked ?? false;

        void (async () => {
          try {
            cfg = await updatePaymentsConfig({
              limits: {
                depositMinCents: int('depositMinCents'),
                depositMaxCents: int('depositMaxCents'),
                depositFeeBps: int('depositFeeBps'),
                depositKycThresholdCents: int('depositKycThresholdCents'),
                requireVerifiedEmail: cb('requireVerifiedEmail'),
                withdrawalMinCents: int('withdrawalMinCents'),
                withdrawalMaxCents: int('withdrawalMaxCents'),
                withdrawalFeeBps: int('withdrawalFeeBps'),
                capToDeposits: cb('capToDeposits'),
              },
            });
            if (!live) return;
            ctx.notify('Limits saved.');
          } catch (err) {
            if (!live) return;
            ctx.notify(errorText(err), 'err');
          }
          paint();
        })();
        return;
      }

      if (form.dataset.form === 'invoices') {
        const val = (name: string) =>
          form.querySelector<HTMLInputElement>(`[name="${name}"]`)?.value ?? '';
        const cb = (name: string) =>
          form.querySelector<HTMLInputElement>(`[name="${name}"]`)?.checked ?? false;

        void (async () => {
          try {
            cfg = await updatePaymentsConfig({
              invoices: {
                prefix: val('invoicePrefix'),
                creditNotePrefix: val('creditNotePrefix'),
                emailEnabled: cb('invoiceEmailEnabled'),
              },
            });
            if (!live) return;
            ctx.notify('Invoice settings saved.');
          } catch (err) {
            if (!live) return;
            ctx.notify(errorText(err), 'err');
          }
          paint();
        })();
      }
    };

    host.addEventListener('click', onClick);
    host.addEventListener('submit', onSubmit as EventListener);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('submit', onSubmit as EventListener);
    };
  },
};
